import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import {
  deletePack,
  listPacks,
  PACK_LIST_PAGE_SIZE,
  type PackKind,
  type PackListPage,
  type PackRow,
  type PackState,
} from '../lib/packs';
import { useToastStore } from '../components/Toast';
import { formatRelativeTimeVi } from '../lib/relativeTime';

/**
 * `/packs` — admin pack list with infinite scroll.
 *
 * Layout:
 *   - Header with title + "Tạo pack mới" button.
 *   - Sticky filter / sort bar (kind, state, sort, refresh).
 *   - Table of pack rows: cover, name, kind badge, state pill, version,
 *     asset count, last updated.
 *   - Infinite scroll via an IntersectionObserver sentinel; uses TanStack
 *     Query's `useInfiniteQuery` with cursor pagination from the Worker
 *     (page size 25 per Requirement 21.5).
 *
 * Sorting:
 *   The Worker only orders by `updated_at DESC`. To honor Requirement 21.4
 *   (sort by name / asset count) without forcing a backend rewrite, the
 *   client applies the secondary sort over the rows it has already loaded.
 *   This is an acceptable trade-off because the admin list is small (low
 *   hundreds of packs) and infinite scroll guarantees the user sees every
 *   row eventually. The sort UI surfaces the backend ordering as the
 *   default ("Cập nhật mới nhất").
 *
 * Asset count:
 *   The basic `GET /admin/v1/packs` row does not currently include an
 *   asset count (the Worker returns just the `packs` table projection).
 *   We render "—" for now and rely on the editor to surface the precise
 *   count. TODO: wire a per-pack count once the Worker exposes
 *   `assets_count` on the list row (or add a batched count endpoint).
 *
 * Validates Requirements 21.1, 21.2, 21.3, 21.4, 21.5, 22.1.
 */

type KindFilter = 'all' | PackKind;
type StateFilter = 'all' | PackState;
type SortKey = 'updatedAt' | 'name' | 'assetCount';

export default function PacksListPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('updatedAt');
  const [confirmingDelete, setConfirmingDelete] = useState<PackRow | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (pack: PackRow) =>
      deletePack(pack.id, { force: pack.pack_state !== 'draft' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['packs'] });
      addToast({ severity: 'success', title: 'Đã xoá', message: 'Pack đã được xoá vĩnh viễn' });
      setConfirmingDelete(null);
    },
  });

  // The query key includes the filters so changing kind/state triggers a
  // fresh paginated walk. Sort is purely client-side (see component
  // header), so it does NOT belong in the key.
  const query = useInfiniteQuery<PackListPage, Error>({
    queryKey: ['packs', { kind: kindFilter, state: stateFilter }],
    queryFn: ({ pageParam }) =>
      listPacks({
        kind: kindFilter === 'all' ? undefined : kindFilter,
        state: stateFilter === 'all' ? undefined : stateFilter,
        cursor: typeof pageParam === 'string' ? pageParam : undefined,
        limit: PACK_LIST_PAGE_SIZE,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  // Flatten + apply client-side sort. We reuse the same memoized array for
  // rendering and for empty-state detection so the pipeline is one source.
  const rows = useMemo<PackRow[]>(() => {
    const flat = query.data?.pages.flatMap((p) => p.items) ?? [];
    if (sortKey === 'updatedAt') {
      // Backend already returns updated_at DESC; no client work needed.
      return flat;
    }
    const copy = flat.slice();
    if (sortKey === 'name') {
      copy.sort((a, b) =>
        a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' }),
      );
    } else {
      // Asset count is unavailable on the basic list response — the sort
      // collapses to a stable no-op. Documented in the component header.
      // We keep the option in the UI for forward compatibility.
    }
    return copy;
  }, [query.data, sortKey]);

  // IntersectionObserver-driven infinite scroll. The sentinel is a small
  // div at the end of the table; once it enters the viewport we ask for
  // the next page. We guard against double-firing while a page is already
  // in flight (`isFetchingNextPage`).
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) {
          return;
        }
        if (query.hasNextPage && !query.isFetchingNextPage) {
          void query.fetchNextPage();
        }
      },
      { rootMargin: '256px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-6xl px-6 py-8">
        <header className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-slate-900">
            Quản lý Pack
          </h1>
          <Link
            to="/packs/new"
            className="inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
          >
            + Tạo pack mới
          </Link>
        </header>

        <FilterBar
          kindFilter={kindFilter}
          onKindChange={setKindFilter}
          stateFilter={stateFilter}
          onStateChange={setStateFilter}
          sortKey={sortKey}
          onSortChange={setSortKey}
          isRefreshing={query.isRefetching}
          onRefresh={() => {
            void query.refetch();
          }}
        />

        <PackTable
          rows={rows}
          isLoading={query.isLoading}
          isError={query.isError}
          errorMessage={query.error?.message ?? null}
          isFetchingNextPage={query.isFetchingNextPage}
          hasNextPage={query.hasNextPage ?? false}
          sentinelRef={sentinelRef}
          onRowClick={(id) => navigate(`/packs/${id}`)}
          onRequestDelete={(pack) => setConfirmingDelete(pack)}
        />

        <Dialog.Root
          open={confirmingDelete !== null}
          onOpenChange={(open) => {
            if (!open) setConfirmingDelete(null);
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/40" />
            <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-slate-200 bg-white p-6 shadow-lg focus:outline-none">
              <Dialog.Title className="text-lg font-semibold text-slate-900">
                Xoá pack vĩnh viễn?
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-slate-500">
                {confirmingDelete
                  ? `Pack "${confirmingDelete.name}" và toàn bộ asset sẽ bị xoá vĩnh viễn. Người dùng đang dùng pack này sẽ mất quyền truy cập sau khi catalog làm mới.`
                  : null}
              </Dialog.Description>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(null)}
                  disabled={deleteMutation.isPending}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Huỷ
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirmingDelete) deleteMutation.mutate(confirmingDelete);
                  }}
                  disabled={deleteMutation.isPending}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-red-400"
                >
                  {deleteMutation.isPending ? 'Đang xoá…' : 'Xoá vĩnh viễn'}
                </button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </div>
  );
}

interface FilterBarProps {
  kindFilter: KindFilter;
  onKindChange: (next: KindFilter) => void;
  stateFilter: StateFilter;
  onStateChange: (next: StateFilter) => void;
  sortKey: SortKey;
  onSortChange: (next: SortKey) => void;
  isRefreshing: boolean;
  onRefresh: () => void;
}

function FilterBar({
  kindFilter,
  onKindChange,
  stateFilter,
  onStateChange,
  sortKey,
  onSortChange,
  isRefreshing,
  onRefresh,
}: FilterBarProps): JSX.Element {
  return (
    <div className="sticky top-0 z-10 -mx-6 mt-6 mb-4 border-b border-slate-200 bg-slate-50/95 px-6 py-3 backdrop-blur">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label
            htmlFor="filter-kind"
            className="block text-xs font-medium uppercase tracking-wide text-slate-500"
          >
            Loại
          </label>
          <select
            id="filter-kind"
            value={kindFilter}
            onChange={(e) => onKindChange(e.target.value as KindFilter)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          >
            <option value="all">Tất cả</option>
            <option value="sticker">Sticker</option>
            <option value="gif">GIF</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="filter-state"
            className="block text-xs font-medium uppercase tracking-wide text-slate-500"
          >
            Trạng thái
          </label>
          <select
            id="filter-state"
            value={stateFilter}
            onChange={(e) => onStateChange(e.target.value as StateFilter)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          >
            <option value="all">Tất cả</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="unpublished">Unpublished</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="filter-sort"
            className="block text-xs font-medium uppercase tracking-wide text-slate-500"
          >
            Sắp xếp
          </label>
          <select
            id="filter-sort"
            value={sortKey}
            onChange={(e) => onSortChange(e.target.value as SortKey)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          >
            <option value="updatedAt">Cập nhật mới nhất</option>
            <option value="name">Tên A-Z</option>
            <option value="assetCount">Số asset</option>
          </select>
        </div>

        <div className="ml-auto">
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isRefreshing ? 'Đang làm mới…' : 'Làm mới'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface PackTableProps {
  rows: PackRow[];
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  sentinelRef: React.RefObject<HTMLDivElement>;
  onRowClick: (id: string) => void;
  onRequestDelete: (pack: PackRow) => void;
}

function PackTable({
  rows,
  isLoading,
  isError,
  errorMessage,
  isFetchingNextPage,
  hasNextPage,
  sentinelRef,
  onRowClick,
  onRequestDelete,
}: PackTableProps): JSX.Element {
  if (isLoading) {
    return <PackListSkeleton />;
  }
  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-md border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700"
      >
        <p className="font-medium">Không tải được danh sách pack.</p>
        {errorMessage ? (
          <p className="mt-1 text-xs text-red-600">{errorMessage}</p>
        ) : null}
      </div>
    );
  }
  if (rows.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th scope="col" className="w-20 px-4 py-3">
              Cover
            </th>
            <th scope="col" className="px-4 py-3">
              Tên pack
            </th>
            <th scope="col" className="px-4 py-3">
              Loại
            </th>
            <th scope="col" className="px-4 py-3">
              Trạng thái
            </th>
            <th scope="col" className="px-4 py-3">
              Phiên bản
            </th>
            <th scope="col" className="px-4 py-3">
              Giá
            </th>
            <th scope="col" className="px-4 py-3">
              Số asset
            </th>
            <th scope="col" className="px-4 py-3">
              Cập nhật
            </th>
            <th scope="col" className="px-4 py-3 text-right">
              {/* Actions */}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <PackTableRow key={row.id} row={row} onClick={onRowClick} onRequestDelete={onRequestDelete} />
          ))}
        </tbody>
      </table>

      {/* Infinite scroll sentinel + footer status. */}
      <div
        ref={sentinelRef}
        className="flex items-center justify-center px-4 py-4 text-xs text-slate-500"
      >
        {isFetchingNextPage
          ? 'Đang tải thêm…'
          : hasNextPage
            ? ''
            : 'Đã hiển thị toàn bộ pack'}
      </div>
    </div>
  );
}

interface PackTableRowProps {
  row: PackRow;
  onClick: (id: string) => void;
  onRequestDelete: (pack: PackRow) => void;
}

function PackTableRow({ row, onClick, onRequestDelete }: PackTableRowProps): JSX.Element {
  const priceLabel = row.price_vnd > 0
    ? `${new Intl.NumberFormat('vi-VN').format(row.price_vnd)}đ`
    : 'Miễn phí';
  return (
    <tr
      onClick={() => onClick(row.id)}
      className="cursor-pointer transition-colors hover:bg-slate-50"
    >
      <td className="px-4 py-3">
        <CoverThumb pack={row} />
      </td>
      <td className="px-4 py-3 font-medium text-slate-900">
        <div>{row.name}</div>
        <div className="text-xs font-normal text-slate-500">{row.id}</div>
      </td>
      <td className="px-4 py-3">
        <KindBadge kind={row.kind} />
      </td>
      <td className="px-4 py-3">
        <StatePill state={row.pack_state} />
      </td>
      <td className="px-4 py-3 tabular-nums text-slate-700">
        v{row.active_version}
      </td>
      <td className="px-4 py-3 tabular-nums text-slate-700">
        {priceLabel}
      </td>
      <td className="px-4 py-3 tabular-nums text-slate-500">—</td>
      <td className="px-4 py-3 text-slate-600">
        {formatRelativeTimeVi(row.updated_at)}
      </td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRequestDelete(row);
          }}
          aria-label={`Xoá ${row.name}`}
          title="Xoá pack"
          className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
        >
          🗑
        </button>
      </td>
    </tr>
  );
}

function CoverThumb({ pack }: { pack: PackRow }): JSX.Element {
  // The Pages catch-all proxy attaches the bearer cookie, so a relative
  // `/admin/v1/...` URL works for `<img src>` inside the dashboard. The
  // upstream Worker exposes per-asset bytes via the public route — but
  // for unpublished drafts we'd need the admin variant. Wire to the
  // admin path so the image loads regardless of pack state. The catch-all
  // proxy route does not currently expose `GET /admin/v1/packs/:id/assets/:id`,
  // so unpublished covers fall back to the placeholder until task 14 (the
  // editor) lands the dedicated route.
  if (pack.cover_asset_id) {
    return (
      <img
        src={`/admin/v1/packs/${pack.id}/assets/${pack.cover_asset_id}`}
        alt={`Ảnh bìa của ${pack.name}`}
        className="h-14 w-14 rounded-md border border-slate-200 object-cover"
        loading="lazy"
        onError={(e) => {
          // If the cover fails to load (e.g. the route 404s for drafts),
          // hide the broken image — the sibling placeholder will show
          // through if we render both.
          (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
        }}
      />
    );
  }
  return <PlaceholderThumb name={pack.name} />;
}

function PlaceholderThumb({ name }: { name: string }): JSX.Element {
  // Single-letter placeholder so packs without a cover are still visually
  // distinguishable in the list. Neutral palette to keep the table calm.
  const letter = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <div
      aria-hidden
      className="flex h-14 w-14 items-center justify-center rounded-md border border-slate-200 bg-slate-100 text-lg font-semibold text-slate-500"
    >
      {letter}
    </div>
  );
}

function KindBadge({ kind }: { kind: PackKind }): JSX.Element {
  const label = kind === 'sticker' ? 'Sticker' : 'GIF';
  return (
    <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700">
      {label}
    </span>
  );
}

function StatePill({ state }: { state: PackState }): JSX.Element {
  // Color buckets per the design: draft = grey, published = green,
  // unpublished = amber. Vietnamese labels for the dashboard surface.
  const config: Record<PackState, { label: string; classes: string }> = {
    draft: {
      label: 'Bản nháp',
      classes: 'bg-slate-100 text-slate-700 border-slate-200',
    },
    published: {
      label: 'Đã xuất bản',
      classes: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    },
    unpublished: {
      label: 'Đã gỡ',
      classes: 'bg-amber-50 text-amber-700 border-amber-200',
    },
  };
  const { label, classes } = config[state];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${classes}`}
    >
      {label}
    </span>
  );
}

function PackListSkeleton(): JSX.Element {
  // Six rows of skeleton bars roughly matching the actual table layout.
  // Fixed to six so the loading state keeps a stable height across
  // responsive widths without flashing.
  return (
    <div
      role="status"
      aria-label="Đang tải danh sách pack"
      className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
    >
      <div className="grid grid-cols-7 gap-4 border-b border-slate-100 bg-slate-50 px-4 py-3 text-xs uppercase tracking-wide text-slate-400">
        <div>Cover</div>
        <div className="col-span-2">Tên pack</div>
        <div>Loại</div>
        <div>Trạng thái</div>
        <div>Phiên bản</div>
        <div>Cập nhật</div>
      </div>
      <div className="divide-y divide-slate-100">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="grid grid-cols-7 items-center gap-4 px-4 py-3">
            <div className="h-14 w-14 animate-pulse rounded-md bg-slate-100" />
            <div className="col-span-2 space-y-2">
              <div className="h-4 w-3/4 animate-pulse rounded bg-slate-100" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
            </div>
            <div className="h-5 w-16 animate-pulse rounded bg-slate-100" />
            <div className="h-5 w-20 animate-pulse rounded-full bg-slate-100" />
            <div className="h-4 w-10 animate-pulse rounded bg-slate-100" />
            <div className="h-4 w-24 animate-pulse rounded bg-slate-100" />
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
      <div className="text-base font-medium text-slate-900">
        Chưa có pack nào
      </div>
      <p className="mt-1 max-w-sm text-sm text-slate-500">
        Tạo pack mới để bắt đầu thêm sticker hoặc GIF cho người dùng Skey.
      </p>
      <Link
        to="/packs/new"
        className="mt-4 inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800"
      >
        + Tạo pack mới
      </Link>
    </div>
  );
}
